import type { NextRequest } from 'next/server'

export const MCP_OAUTH_CORRELATION_PARAM = '__superscale_mcp_correlation'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const MCP_OAUTH_PATHS = new Set(['/__clerk/oauth/authorize', '/__clerk/oauth/authorize/continue'])

export type IMcpOAuthServerStage = 'authorization_bridge' | 'authorization_resume' | 'continuation_proxy'

/** Returns a validated Superscale-generated OAuth correlation identifier. */
export function getMcpOAuthCorrelationId(url: URL) {
    const value = url.searchParams.get(MCP_OAUTH_CORRELATION_PARAM)
    return value && UUID_PATTERN.test(value) ? value : undefined
}

/** Creates a bounded correlation identifier for an MCP OAuth transaction. */
export function createMcpOAuthCorrelationId() {
    return crypto.randomUUID()
}

/** Builds the allowlisted server fields used to trace MCP OAuth routing. */
export function buildMcpOAuthServerLogFields(
    request: NextRequest,
    stage: IMcpOAuthServerStage,
    correlationId = getMcpOAuthCorrelationId(request.nextUrl)
) {
    return {
        event: 'mcp_oauth.server_stage',
        mcpOauthStage: stage,
        ...(correlationId ? { mcpOauthCorrelationId: correlationId } : {}),
        requestMethod: request.method,
        requestHost: request.nextUrl.hostname,
        requestPath: request.nextUrl.pathname,
        hasResource: request.nextUrl.searchParams.has('resource'),
        hasResumeMarker: request.nextUrl.searchParams.has('__superscale_mcp_sign_in'),
        hasClerkClientCookie: request.cookies.has('__client'),
        hasClerkSessionCookie: request.cookies.has('__session'),
        hasClerkDevelopmentBrowserCookie: request.cookies.has('__clerk_db_jwt'),
    }
}

/** Emits one privacy-safe server event for an MCP OAuth routing stage. */
export function logMcpOAuthServerStage(
    request: NextRequest,
    stage: IMcpOAuthServerStage,
    correlationId?: string
) {
    console.info(JSON.stringify(buildMcpOAuthServerLogFields(request, stage, correlationId)))
}

/** Builds browser-safe fields when post-authentication navigation resumes MCP OAuth. */
export function buildMcpOAuthBrowserLogFields(redirectUrl: string, stage: 'session_activation' | 'fallback_navigation') {
    let target: URL
    try {
        target = new URL(redirectUrl, typeof window === 'undefined' ? 'https://superscale.invalid' : window.location.origin)
    } catch {
        return undefined
    }

    if (!MCP_OAUTH_PATHS.has(target.pathname)) return undefined

    const correlationId = getMcpOAuthCorrelationId(target)
    return {
        event: 'mcp_oauth.browser_stage',
        mcpOauthStage: stage,
        ...(correlationId ? { mcpOauthCorrelationId: correlationId } : {}),
        targetHost: target.hostname,
        targetPath: target.pathname,
        hasResource: target.searchParams.has('resource'),
        hasResumeMarker: target.searchParams.has('__superscale_mcp_sign_in'),
    }
}
